import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Users2,
  ClipboardList,
  Boxes,
  Layers,
  ListChecks,
  FileImage,
  Network,
  ListTodo,
  GraduationCap,
  Building2,
  Workflow,
  Sparkles,
  PackagePlus,
  BarChart3,
  Film,
  Menu,
  X,
  MessageSquare,
  FileBadge,
  ShieldCheck,
  ChevronDown,
  Palette,
  ContactRound,
  KeyRound,
  Megaphone,
  Mail,
  CreditCard,
  Calculator,
  Lightbulb,
  Settings2,
  Stethoscope,
  HeartPulse,
  Library,
  Rocket,
  Radar,
  FileText,
  Printer,
  Activity,
  Brain,
  TrendingUp,
  UserPlus,
  ShieldAlert,
  AlertOctagon,
} from "lucide-react";

// Grouped navigation. Top-level items with `children` collapse; items
// without `children` (Overview, Messages) render flat.
//
// Routes are unchanged so deep-links keep working — only labels and grouping
// were restructured per Feb 2026 admin redesign.
const NAV = [
  {
    id: "overview",
    label: "Command Center",
    icon: LayoutDashboard,
    to: "/admin/dashboard",
  },
  {
    id: "founder-dashboard",
    label: "Founder Dashboard",
    icon: TrendingUp,
    to: "/admin/founder-dashboard",
  },
  {
    id: "founder-os",
    label: "Execute the Vision",
    icon: Rocket,
    to: "/admin/execute",
  },
  {
    id: "opportunity-engine",
    label: "Opportunity Engine",
    icon: Radar,
    to: "/admin/opportunity",
  },
  {
    id: "knowledge-base",
    label: "Knowledge Base",
    icon: Brain,
    to: "/admin/knowledge-base",
  },
  {
    id: "messages",
    label: "Messages",
    icon: MessageSquare,
    to: "/admin/messages",
  },
  {
    id: "product",
    label: "Product",
    icon: HeartPulse,
    children: [
      { id: "screening-tools", label: "Screening Tools", icon: ListChecks, to: "/admin/screeners" },
      { id: "packages", label: "Programs", icon: Layers, to: "/admin/programs" },
      { id: "services-products", label: "Services", icon: Boxes, to: "/admin/services" },
    ],
  },
  {
    id: "clinics",
    label: "Clinics",
    icon: Stethoscope,
    children: [
      { id: "clinic-roster", label: "Clinic Roster", icon: ContactRound, to: "/admin/clinic-roster" },
      { id: "branding", label: "Branding", icon: Palette, to: "/admin/clinics" },
    ],
  },
  {
    id: "vendors",
    label: "Vendors",
    icon: Network,
    children: [
      { id: "vendor-roster", label: "Vendor Network", icon: ContactRound, to: "/admin/vendors-directory" },
      { id: "vendor-services", label: "Vendor Services", icon: ShieldCheck, to: "/admin/vendor-services" },
      { id: "vendor-pipeline", label: "Vendor Pipeline", icon: Users2, to: "/admin/vendors" },
    ],
  },
  {
    id: "operations",
    label: "Operations",
    icon: Settings2,
    children: [
      { id: "analytics", label: "Analytics", icon: BarChart3, to: "/admin/analytics" },
      { id: "website-analytics", label: "Website Analytics", icon: BarChart3, to: "/admin/website-analytics" },
      { id: "todos", label: "To Do List", icon: ListTodo, to: "/admin/todos" },
      { id: "workflow-engine", label: "Workflow Engine", icon: Workflow, to: "/admin/workflow-engine" },
      { id: "vendor-intake", label: "Vendor Intake Manager", icon: ListChecks, to: "/admin/vendor-intake" },
      { id: "stripe-connect", label: "Stripe Connect", icon: CreditCard, to: "/admin/stripe-connect" },
      { id: "payments", label: "Payments", icon: Calculator, to: "/admin/payments" },
      { id: "program-requests", label: "Program Requests", icon: ClipboardList, to: "/admin/program-requests" },
      { id: "documents", label: "Documents", icon: FileBadge, to: "/admin/documents" },
      { id: "email", label: "Email", icon: Mail, to: "/admin/email" },
      { id: "system-health", label: "System Health", icon: Activity, to: "/admin/system-health" },
    ],
  },
  {
    id: "marketing",
    label: "Marketing",
    icon: Megaphone,
    children: [
      { id: "marketing-assets", label: "Marketing Assets", icon: FileImage, to: "/admin/marketing" },
      { id: "campaign-manager", label: "Campaign Manager", icon: Megaphone, to: "/admin/campaign-manager" },
      { id: "blog", label: "Blog", icon: FileText, to: "/admin/blog" },
      { id: "roi-calculator", label: "ROI Calculator (demo)", icon: Calculator, to: "/admin/roi-calculator" },
      { id: "marketing-flyer", label: "Sales Flyer (print)", icon: Printer, to: "/admin/marketing-flyer" },
      { id: "marketing-whitepapers", label: "White Papers", icon: FileText, to: "/admin/whitepapers" },
      { id: "demo-video", label: "Demo Video", icon: Film, to: "/admin/demo-video" },
      { id: "demo-requests", label: "Demo Requests", icon: ClipboardList, to: "/admin/demos" },
      { id: "demo-access", label: "Demo Access", icon: KeyRound, to: "/admin/demo-access" },
      { id: "portal-invites", label: "Portal Invites", icon: UserPlus, to: "/admin/invites" },
    ],
  },
  {
    id: "resources",
    label: "Resources",
    icon: Library,
    children: [
      { id: "training", label: "Training", icon: GraduationCap, to: "/admin/training" },
      { id: "whitepapers", label: "White Papers", icon: FileText, to: "/admin/whitepapers" },
      { id: "think-tank-digest", label: "Think Tank Digest", icon: Sparkles, to: "/admin/think-tank-digest" },
      { id: "service-requests", label: "Service Requests", icon: PackagePlus, to: "/admin/service-requests" },
    ],
  },
  {
    id: "business",
    label: "Business",
    icon: BarChart3,
    children: [
      { id: "subscriptions", label: "Platform Subscriptions", icon: CreditCard, to: "/admin/subscriptions" },
      { id: "platform-economics", label: "Platform Economics", icon: Calculator, to: "/admin/platform-economics" },
      { id: "platform-strategies", label: "Platform Strategies", icon: Lightbulb, to: "/admin/platform-strategies" },
      { id: "future-considerations", label: "Future Considerations", icon: Sparkles, to: "/admin/future-considerations" },
      { id: "clinic-adoption", label: "Clinic Adoption Playbook", icon: Lightbulb, to: "/admin/clinic-adoption" },
    ],
  },
  {
    id: "integrations",
    label: "Integrations",
    icon: ShieldCheck,
    children: [
      { id: "procurement", label: "Procurement Checklist", icon: ShieldCheck, to: "/admin/procurement" },
    ],
  },
];

const STORAGE_KEY = "nodera-admin-nav-collapsed";

function _readCollapsed() {
  try {
    const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
    return typeof raw === "object" && raw ? raw : {};
  } catch {
    return {};
  }
}

function _isGroupActive(group, pathname) {
  return (group.children || []).some(
    (c) => pathname === c.to || pathname.startsWith(`${c.to}/`),
  );
}

export default function AdminShell() {
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(_readCollapsed);
  const { pathname } = useLocation();

  // Persist collapsed groups across reloads so admins keep their preferred
  // information density. Default = all expanded (empty object).
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(collapsed));
    } catch {
      /* storage may be disabled — fail soft */
    }
  }, [collapsed]);

  const toggleGroup = (id) =>
    setCollapsed((prev) => ({ ...prev, [id]: !prev[id] }));

  return (
    <div className="flex min-h-[calc(100vh-4rem)]" data-testid="admin-shell">
      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-16 left-0 right-0 z-30 bg-white border-b border-slate-200 px-4 py-2.5 flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-blue-600">
          Admin
        </div>
        <button
          type="button"
          data-testid="admin-shell-menu-btn"
          onClick={() => setOpen((v) => !v)}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded-full px-3 py-1.5 hover:bg-slate-50"
        >
          {open ? <X className="w-3.5 h-3.5" /> : <Menu className="w-3.5 h-3.5" />} Menu
        </button>
      </div>

      {/* Sidebar */}
      <aside
        data-testid="admin-shell-sidebar"
        className={`${
          open ? "block" : "hidden"
        } lg:block fixed lg:fixed top-16 left-0 bottom-0 w-64 bg-slate-50 border-r border-slate-200 z-20 overflow-y-auto pt-6 pb-10 px-4`}
      >
        <div className="px-2 mb-4 text-[11px] uppercase tracking-[0.22em] font-semibold text-slate-500">
          Admin · Internal
        </div>
        <nav className="space-y-1">
          {NAV.map((item) =>
            item.children ? (
              <NavGroup
                key={item.id}
                item={item}
                collapsed={!!collapsed[item.id]}
                onToggle={() => toggleGroup(item.id)}
                pathname={pathname}
                onLeafClick={() => setOpen(false)}
              />
            ) : (
              <NavLeaf
                key={item.id}
                item={item}
                exact={item.to === "/admin/dashboard"}
                onClick={() => setOpen(false)}
              />
            ),
          )}
        </nav>
      </aside>

      {/* Main content */}
      <div className="flex-1 lg:pl-64">
        <Outlet />
      </div>
    </div>
  );
}

function NavLeaf({ item, exact = false, onClick, indent = false }) {
  return (
    <NavLink
      to={item.to}
      end={exact}
      data-testid={`admin-nav-${item.id}`}
      onClick={onClick}
      className={({ isActive }) =>
        `flex items-center gap-2.5 ${indent ? "pl-7 pr-3" : "px-3"} py-2 rounded-lg text-sm font-medium transition-colors ${
          isActive
            ? "bg-slate-900 text-white shadow-sm"
            : "text-slate-700 hover:bg-white hover:text-slate-900"
        }`
      }
    >
      <item.icon className="w-4 h-4 shrink-0" />
      <span className="truncate">{item.label}</span>
    </NavLink>
  );
}

function NavGroup({ item, collapsed, onToggle, pathname, onLeafClick }) {
  const groupActive = _isGroupActive(item, pathname);
  // If a child is the current route, force the group open even if the user
  // collapsed it earlier — keeps wayfinding intuitive.
  const isOpen = !collapsed || groupActive;
  return (
    <div data-testid={`admin-nav-group-${item.id}`}>
      <button
        type="button"
        onClick={onToggle}
        data-testid={`admin-nav-group-toggle-${item.id}`}
        className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${
          groupActive
            ? "text-slate-900 bg-white"
            : "text-slate-600 hover:bg-white hover:text-slate-900"
        }`}
        aria-expanded={isOpen}
      >
        <item.icon className="w-4 h-4 shrink-0" />
        <span className="flex-1 text-left truncate">{item.label}</span>
        <ChevronDown
          className={`w-3.5 h-3.5 text-slate-400 transition-transform ${
            isOpen ? "rotate-0" : "-rotate-90"
          }`}
        />
      </button>
      {isOpen && (
        <div className="mt-0.5 mb-1 space-y-0.5">
          {item.children.map((child) => (
            <NavLeaf
              key={child.id}
              item={child}
              indent
              onClick={onLeafClick}
            />
          ))}
        </div>
      )}
    </div>
  );
}
